import { createSelector } from 'reselect';

import * as constants from '../../../components/TaskStatusCard/constants';
import { isTrue } from '../../../utils/commonUtils';
import { safeParseDateString } from '../../../utils/dateUtils';

const isActiveTask = ({ status }) => status === 'taskDisplayStatusStarted';
const isCompletedTask = ({ status }) => status === 'taskDisplayStatusCompleted';
const isPendingTask = ({ status }) => status === 'taskDisplayStatusTBD';

const mapTaskStatus = (task) => {
  if (isActiveTask(task)) {
    return constants.taskStatuses.ACTIVE;
  }
  if (isCompletedTask(task)) {
    return constants.taskStatuses.COMPLETED;
  }
  if (isPendingTask(task)) {
    return constants.taskStatuses.PENDING;
  }
};

const selectTaskDateByStatus = (task) => {
  if (isActiveTask(task) || isPendingTask(task)) {
    return task.dueDate;
  }
  if (isCompletedTask(task)) {
    return task.signedOffDate;
  }
};

const selectUserByStatus = (task) => {
  if (isActiveTask(task) || isPendingTask(task)) {
    return task.ownerName;
  }
  if (isCompletedTask(task)) {
    return task.signedOffByName;
  }
};

const getTaskId = ({ workflowId, phaseId, taskId }) => [workflowId, phaseId, taskId].join('-');

const getTotalSubtasksCount = ({ routedTaskCount, pendingTaskCount, closedTaskCount }) =>
  +routedTaskCount + +pendingTaskCount + +closedTaskCount;

const mapTask = (task) => ({
  id: getTaskId(task),
  isInitialTask: false,
  isTaskPortal: isTrue(task.routedToPortal),
  label: task.taskName,
  date: safeParseDateString(selectTaskDateByStatus(task)),
  user: selectUserByStatus(task),
  status: mapTaskStatus(task),
  completedSubtasksCount: parseInt(task.closedTaskCount),
  totalSubtasksCount: getTotalSubtasksCount(task),
  remainingDays: task.remainingDays,
  overdueDays: task.overdueDays,
});

const mapStartTask = (startTask) => ({
  id: 'start',
  isInitialTask: true,
  label: startTask.label,
  date: safeParseDateString(startTask.createdDate),
  user: startTask.createdBy,
  status: mapTaskStatus(startTask),
  completedSubtasksCount: 0,
  totalSubtasksCount: 0,
});

const getTaskStatusPanel = (state) => state.taskStatusPanel;

export const getLoading = createSelector(
  getTaskStatusPanel,
  (taskStatusPanel) => taskStatusPanel.loading
);

export const getTaskStatusDisabled = createSelector(
  getTaskStatusPanel,
  (taskStatusPanel) => taskStatusPanel.disabled
);

const getStartTask = createSelector(getTaskStatusPanel, (taskStatusPanel) => taskStatusPanel.start);

const getGraph3 = createSelector(getTaskStatusPanel, (taskStatusPanel) => taskStatusPanel.tasks);

const getApplicableTasks = createSelector(getGraph3, (tasks) =>
  tasks.filter((t) => t.isTaskApplicable === 'true')
);

export const getTasks = createSelector(
  [getStartTask, getApplicableTasks],
  (startTask, applicableTasks) => {
    const mappedTasks = applicableTasks.map(mapTask);
    if (startTask) {
      const mappedStartTask = mapStartTask(startTask);
      return [mappedStartTask, ...mappedTasks];
    }
    return mappedTasks;
  }
);
